import { UUID } from 'crypto';
import { convertToDate } from 'odfjell-date-time-utils/dateTimeConverter';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { StowagePlanShareTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanShareTypeEnum';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getInitialsFromName } from 'src/utils/userUtils';
import { deepCopy } from 'src/utils/objectUtils';

export const TITLE_MAX_LENGTH = 50;
export const COMMENTS_MAX_LENGTH = 500;

export const createEmpyStowagePlan = (
    imoNumber: number,
    userName: string,
    parentGuid: UUID | null,
    title: string,
    masterPlanVersion: number
): StowagePlan => {
    return {
        stowagePlanGuid: crypto.randomUUID(),
        parentGuid,
        imoNumber: imoNumber,
        title: title,
        comments: '',
        type: StowagePlanTypeEnum.Draft,
        shareType: StowagePlanShareTypeEnum.Private,
        owner: userName,
        initials: getInitialsFromName(userName),
        lastUpdatedBy: userName,
        lastUpdatedUtc: getDateNow(),
        createdDateTimeUtc: getDateNow(),
        syncedWithImosDateTimeUtc: null,
        syncedWithParentDateTimeUtc: parentGuid ? getDateNow() : null,
        isDeleted: false,
        masterPlanVersion: masterPlanVersion,
        isNewStowagePlan: true,
        stowageItems: [],
        rotation: [],
        cargoOperations: [],
        cargoes: [],
        transitOperations: [],
        transitStowageItems: [],
        adjustedCargoTemperatures: [],
        blockedTanks: [],
    };
};

export const createInitialMasterPlan = (imoNumber: number, vesselName: string, voyageNumber: string, userName: string): StowagePlan => {
    const title = `${vesselName} ${voyageNumber}`;
    return {
        stowagePlanGuid: crypto.randomUUID(),
        parentGuid: null,
        imoNumber,
        title,
        comments: `${title} - Initial master plan`,
        type: StowagePlanTypeEnum.Master,
        shareType: StowagePlanShareTypeEnum.Shared,
        owner: userName,
        initials: getInitialsFromName(userName),
        lastUpdatedBy: userName,
        lastUpdatedUtc: getDateNow(),
        createdDateTimeUtc: getDateNow(),
        syncedWithImosDateTimeUtc: null,
        syncedWithParentDateTimeUtc: null,
        isDeleted: false,
        masterPlanVersion: 1,
        isNewStowagePlan: true,
        stowageItems: [],
        rotation: [],
        cargoOperations: [],
        cargoes: [],
        transitOperations: [],
        transitStowageItems: [],
        adjustedCargoTemperatures: [],
        blockedTanks: [],
    };
};

export const copyNewStowagePlanMetaData = (originalStowagePlan: StowagePlan, newStowagePlan: StowagePlan) => {
    return {
        ...originalStowagePlan,
        lastUpdatedBy: newStowagePlan.lastUpdatedBy,
        lastUpdatedUtc: newStowagePlan.lastUpdatedUtc,
        createdDateTimeUtc: newStowagePlan.createdDateTimeUtc,
        isNewStowagePlan: null,
    };
};
export const copyUpdatedStowagePlanMetaData = (originalStowagePlan: StowagePlan, updatedStowagePlan: StowagePlan) => {
    return {
        ...originalStowagePlan,
        lastUpdatedBy: updatedStowagePlan.lastUpdatedBy,
        lastUpdatedUtc: updatedStowagePlan.lastUpdatedUtc,
        createdDateTimeUtc: updatedStowagePlan.createdDateTimeUtc,
        title: updatedStowagePlan.title,
        comments: updatedStowagePlan.comments,
        type: updatedStowagePlan.type,
        shareType: updatedStowagePlan.shareType,
        owner: updatedStowagePlan.owner,
        initials: updatedStowagePlan.initials,
        syncedWithParentDateTimeUtc: updatedStowagePlan.syncedWithParentDateTimeUtc,
        syncedWithImosDateTimeUtc: updatedStowagePlan.syncedWithImosDateTimeUtc,
        isDeleted: updatedStowagePlan.isDeleted,
        masterPlanVersion: updatedStowagePlan.masterPlanVersion,
        isNewStowagePlan: null,
    };
};
export const cleanStowagePlanData = (stowagePlan: StowagePlan) => {
    return {
        ...stowagePlan,
        stowageItems: [],
        rotation: [],
        cargoOperations: [],
        cargoes: [],
        transitOperations: [],
        transitStowageItems: [],
        adjustedCargoTemperatures: [],
        blockedTanks: [],
        berthCalls: [],
    };
};

export const mapStowagePlan = (bm: StowagePlanBM): StowagePlan => {
    return {
        stowagePlanGuid: bm.stowagePlanGuid,
        parentGuid: bm.parentGuid,
        imoNumber: bm.imoNumber,
        title: bm.title,
        comments: bm.comments,
        type: bm.type,
        shareType: bm.shareType,
        owner: bm.owner,
        initials: bm.initials,
        lastUpdatedBy: bm.lastUpdatedBy,
        lastUpdatedUtc: convertToDate(bm.lastUpdatedUtc),
        createdDateTimeUtc: convertToDate(bm.createdDateTimeUtc),
        syncedWithImosDateTimeUtc: bm.syncedWithImosDateTimeUtc ? convertToDate(bm.syncedWithImosDateTimeUtc) : null,
        syncedWithParentDateTimeUtc: bm.syncedWithParentDateTimeUtc ? convertToDate(bm.syncedWithParentDateTimeUtc) : null,
        isDeleted: bm.isDeleted,
        masterPlanVersion: bm.masterPlanVersion,
        stowageItems: [],
        rotation: [],
        cargoOperations: [],
        cargoes: [],
        transitOperations: [],
        transitStowageItems: [],
        adjustedCargoTemperatures: [],
        blockedTanks: [],
    };
};
export const mapStowagePlanBM = (stowagePlan: StowagePlan): StowagePlanBM => {
    return {
        stowagePlanGuid: stowagePlan.stowagePlanGuid,
        parentGuid: stowagePlan.parentGuid,
        imoNumber: stowagePlan.imoNumber,
        title: stowagePlan.title.length > TITLE_MAX_LENGTH ? stowagePlan.title.substring(0, TITLE_MAX_LENGTH) : stowagePlan.title,
        comments: stowagePlan.comments.length > COMMENTS_MAX_LENGTH ? stowagePlan.comments.substring(0, COMMENTS_MAX_LENGTH) : stowagePlan.comments,
        type: stowagePlan.type,
        shareType: stowagePlan.shareType,
        owner: stowagePlan.owner,
        initials: stowagePlan.initials,
        lastUpdatedBy: stowagePlan.lastUpdatedBy,
        lastUpdatedUtc: stowagePlan.lastUpdatedUtc,
        createdDateTimeUtc: stowagePlan.createdDateTimeUtc,
        syncedWithParentDateTimeUtc: stowagePlan.syncedWithParentDateTimeUtc,
        syncedWithImosDateTimeUtc: stowagePlan.syncedWithImosDateTimeUtc,
        isDeleted: stowagePlan.isDeleted,
        masterPlanVersion: stowagePlan.masterPlanVersion,
    };
};

export const deepCopyStowagePlan = (stowagePlan: StowagePlan): StowagePlan => {
    if (!stowagePlan) return null;
    return deepCopy<StowagePlan>(stowagePlan);
};

export const mapStowagePlans = (bms: StowagePlanBM[]) => {
    return bms ? bms.map(mapStowagePlan) : [];
};
